/**
 * assets/wc-session.js
 * Session, auth, and merge helpers.
 * Lifted from wolden-customize.js lines ~708-1219 with zero behavior change.
 *
 * Depends on:
 *   ./wc-analytics.js  — log, track
 *   ./wc-state.js      — getSessionId (re-exported as-is)
 *
 * Modal UI callbacks (setModalState, startLoadingTicker, showToast, etc.) are
 * injected at call-sites via the `callbacks` argument rather than imported here,
 * because those functions live in the monolith's modal layer (Phase 5+ extract).
 * This keeps wc-session.js free of any DOM/modal dependency.
 *
 * Orchestrator URL chooser is replicated verbatim — Sub-project C will
 * centralise it into wc-config.js and remove the duplication.
 */

import { log, track } from './wc2-analytics.js';
import { getSessionId as _getSessionId } from './wc2-state.js';

// ─── Orchestrator constants (replicated from monolith lines 128-151) ──────────
// Sub-project C will centralise these into wc-config.js.
var ORCHESTRATOR_V1_URL = 'https://pboljz7py4yztpystvet2qcryi0egncw.lambda-url.us-east-2.on.aws/';
var ORCHESTRATOR_V2_URL = 'https://lyzv32bofp5symutf7snoe2rxi0avlzj.lambda-url.us-east-2.on.aws/';

function wcChooseOrchestratorUrl() {
  // URL param overrides everything (dev mode)
  try {
    var params = new URLSearchParams(window.location.search);
    var paramVal = params.get('wc_v2');
    if (paramVal === '1') return ORCHESTRATOR_V2_URL;
    if (paramVal === '0') return ORCHESTRATOR_V1_URL;
  } catch (_) {}
  // Theme setting
  if (window.WC_SETTINGS && window.WC_SETTINGS.enable_orchestrator_v2 === true) {
    return ORCHESTRATOR_V2_URL;
  }
  return ORCHESTRATOR_V1_URL;
}

var ORCHESTRATOR_URL   = wcChooseOrchestratorUrl();
var ORCHESTRATOR_TOKEN = 'q2BzB8woX0dgbBXsr-0TtQorjWz8lXi81Cc64RYtXvM';

// Phase 12 — Google Identity Services client id.
// Public by design (Google's docs explicitly say to embed in client JS).
var GOOGLE_OAUTH_CLIENT_ID = '901844962529-in047nmvdfs0bqij3st6e6iuhkfcm9io.apps.googleusercontent.com';

// ─── Session cookie name (shared with monolith during transition) ─────────────
var SESSION_COOKIE = 'wolden_session_id';

// ─── Email localStorage keys (Phase 8) ────────────────────────────────────────
var EMAIL_FLAG_KEY = 'wolden_email_captured';
var EMAIL_VAL_KEY  = 'wolden_email';

// ─── Pending-gen localStorage keys (Shopify login flow) ───────────────────────
var PENDING_GEN_KEY   = 'wolden_pending_gen_payload';
var PENDING_EMAIL_KEY = 'wolden_pending_email';
var POST_LOGIN_TTL_MS = 30 * 60 * 1000; // 30 min

// ─── GIS state ────────────────────────────────────────────────────────────────
var _gisLoadPromise = null;

// ─────────────────────────────────────────────────────────────────────────────
// Public exports
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Re-export getSessionId from wc-state so callers only need one import.
 * Reads the wolden_session_id cookie; creates + persists a new UUID if absent.
 */
export function getSessionId() {
  return _getSessionId();
}

/**
 * Returns the Shopify customer id if the current visitor is logged in,
 * else null. Value is injected by Liquid into window.WoldenShopifyCustomerId.
 */
export function getCurrentCustomer() {
  return (window.WoldenShopifyCustomerId && String(window.WoldenShopifyCustomerId)) || null;
}

/**
 * Marks the user's email as captured and persists it to localStorage.
 * Mirrors the monolith's setEmailCaptured(email) (Phase 8).
 *
 * @param {string} email
 */
export function captureEmail(email) {
  try {
    localStorage.setItem(EMAIL_FLAG_KEY, '1');
    if (email) localStorage.setItem(EMAIL_VAL_KEY, email);
  } catch (e) { /* ignore */ }
}

/**
 * Returns true if the user has previously submitted their email
 * (flag persisted across sessions in localStorage).
 */
export function isEmailCaptured() {
  try { return localStorage.getItem(EMAIL_FLAG_KEY) === '1'; } catch (e) { return false; }
}

/**
 * Returns the most-recently captured email address, or null.
 */
export function getCapturedEmail() {
  try { return localStorage.getItem(EMAIL_VAL_KEY) || null; } catch (e) { return null; }
}

/**
 * Server-side session merge: link the anonymous wolden_session_id cookie to
 * the Shopify customer id so historical generations follow them.
 *
 * Idempotent on the server; throttled client-side to at most once per 30 s
 * per (sessionId, customerId) pair.
 *
 * Lifted verbatim from monolith maybeRunSessionMerge() (~lines 857-893).
 * Refactored into an exported async function so callers can await if desired;
 * fire-and-forget callers should call .catch(() => {}).
 *
 * @param {string} sessionId
 * @param {string} customerId
 * @returns {Promise<{skipped?: string}>}
 */
export function mergeSessionToCustomer(sessionId, customerId) {
  if (!sessionId || !customerId) return Promise.resolve({ skipped: 'missing-args' });

  var throttleKey = 'wolden_session_merge_lastrun_' + customerId + '_' + sessionId;
  try {
    var lastRun = parseInt(localStorage.getItem(throttleKey) || '0', 10);
    if (lastRun && (Date.now() - lastRun) < 30 * 1000) {
      return Promise.resolve({ skipped: 'throttled' });
    }
  } catch (e) { /* ignore */ }

  log('session-merge: posting', { sessionId: sessionId, customerId: customerId });
  return fetch(ORCHESTRATOR_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: ORCHESTRATOR_TOKEN,
      action: 'session-merge',
      woldenSessionId: sessionId,
      shopifyCustomerId: customerId,
    }),
  }).then(function (resp) {
    if (resp.ok) {
      try { localStorage.setItem(throttleKey, String(Date.now())); } catch (e) { /* ignore */ }
    }
    return { ok: resp.ok };
  }).catch(function (err) {
    log('session-merge error', err && err.message);
    return { error: err && err.message };
  });
}

/**
 * Boot-time helper: if a Shopify customer is logged in, fire a session merge.
 * Called on every page load from the monolith boot sequence (line 7227).
 * Throttle logic prevents hammering on SPA-style navigations.
 *
 * Lifted verbatim from monolith maybeRunSessionMerge() shell.
 */
export function maybeRunSessionMerge() {
  var customerId = getCurrentCustomer();
  if (!customerId) return;
  var sessionId = (function () {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + SESSION_COOKIE + '=([^;]+)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  })();
  if (!sessionId) return;
  mergeSessionToCustomer(sessionId, customerId).catch(function () {});
}

/**
 * Hook: call when a customer signs in (Google or Shopify).
 * Fires session-merge in the background and records a tracking event.
 *
 * @param {string} customerId  Shopify customer id
 */
export function onSignIn(customerId) {
  if (!customerId) return;
  var sid = getSessionId();
  mergeSessionToCustomer(sid, customerId).catch(function () {});
}

// ─── Google Identity Services ─────────────────────────────────────────────────

/**
 * Lazy-load the GIS script. Returns a Promise that resolves when
 * window.google.accounts.id is available. Subsequent calls return the same
 * Promise (singleton pattern).
 *
 * Lifted verbatim from monolith lines 716-734.
 *
 * @returns {Promise<void>}
 */
export function loadGoogleIdentityServices() {
  if (_gisLoadPromise) return _gisLoadPromise;
  _gisLoadPromise = new Promise(function (resolve, reject) {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      resolve(); return;
    }
    var s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = function () {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        resolve();
      } else {
        reject(new Error('GIS loaded but global missing'));
      }
    };
    s.onerror = function () { reject(new Error('Failed to load Google Identity Services')); };
    document.head.appendChild(s);
  });
  return _gisLoadPromise;
}

/**
 * Render the Google Sign-In button into a container element.
 * Clears and re-renders if called again (re-callable).
 *
 * Lifted verbatim from monolith renderGoogleSignInButton() (lines 736-772).
 *
 * @param {HTMLElement}  container    Target element for the button.
 * @param {function}     onCredential Callback(googleResp, retryPayload).
 * @param {*}            retryPayload Deferred-gen payload to thread through.
 */
export function renderGoogleSignInButton(container, onCredential, retryPayload) {
  if (!container) return;
  loadGoogleIdentityServices().then(function () {
    window.google.accounts.id.initialize({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      callback: function (resp) {
        onCredential(resp, retryPayload);
      },
      ux_mode: 'popup',
      // We use the explicit button — disable One Tap (would auto-prompt).
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    container.innerHTML = '';
    window.google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      type: 'standard',
      text: 'continue_with',
      shape: 'pill',
      logo_alignment: 'left',
      width: Math.min(360, Math.max(240, container.offsetWidth || 320)),
    });
    track('google_signin_rendered');
  }).catch(function (err) {
    log('GIS render error', err && err.message);
    container.innerHTML = '<small style="color:#999">Google sign-in unavailable. Use email instead.</small>';
  });
}

/**
 * Handle a Google credential response: POST the id_token to the orchestrator's
 * google-signin action, then invoke callbacks on success/failure.
 *
 * Lifted verbatim from monolith handleGoogleCredential() (lines 773-824).
 *
 * @param {object} googleResp        The credential object from GIS callback.
 * @param {*}      retryPayload      Deferred-gen payload (may be null).
 * @param {object} callbacks
 * @param {HTMLElement|null} callbacks.errEl       Element to write error text into.
 * @param {function}         callbacks.onSuccess   Called with {customerId, email, retryPayload}.
 * @param {function}         callbacks.onFailure   Called with {error}.
 */
export function handleGoogleCredential(googleResp, retryPayload, callbacks) {
  var idToken = googleResp && googleResp.credential;
  if (!idToken) return;

  var cb = callbacks || {};
  track('google_signin_credential_received');

  if (cb.errEl) cb.errEl.textContent = 'Signing in…';

  fetch(ORCHESTRATOR_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: ORCHESTRATOR_TOKEN,
      action: 'google-signin',
      idToken: idToken,
      woldenSessionId: getSessionId(),
    }),
  }).then(function (resp) {
    return resp.json().then(function (body) { return { resp: resp, body: body }; });
  }).then(function (r) {
    if (!r.resp.ok || !r.body.success) {
      var msg = (r.body && r.body.error) || ('HTTP ' + r.resp.status);
      if (cb.errEl) cb.errEl.textContent = 'Google sign-in failed: ' + msg;
      track('google_signin_failed', { error: msg });
      if (cb.onFailure) cb.onFailure({ error: msg });
      return;
    }
    // Success — persist customer id + email.
    window.WoldenShopifyCustomerId = r.body.customerId;
    captureEmail(r.body.email);
    track('google_signin_succeeded', {
      is_new_customer: r.body.isNewCustomer,
      session_merged: r.body.sessionMerged,
    });
    if (cb.errEl) cb.errEl.textContent = '';
    if (cb.onSuccess) {
      cb.onSuccess({
        customerId: r.body.customerId,
        email: r.body.email,
        retryPayload: retryPayload,
      });
    }
  }).catch(function (err) {
    log('google-signin POST error', err && err.message);
    if (cb.errEl) cb.errEl.textContent = 'Connection issue — please try again.';
    track('google_signin_failed', { error: 'network' });
    if (cb.onFailure) cb.onFailure({ error: 'network' });
  });
}

// ─── Shopify Customer Accounts login flow ─────────────────────────────────────
// 2026-05-13 — Replaces the prior magic-link-only flow with Shopify's
// hosted New Customer Accounts login (OTP via shop.account.myshopify.com).

/**
 * Save the pending gen payload + email to localStorage, then redirect the
 * browser to Shopify's /account/login. On return, maybeResumePostLogin()
 * picks up the payload and fires the deferred generation.
 *
 * Lifted verbatim from monolith startShopifyLogin() (lines 923-954).
 *
 * @param {string} email        Email the user typed.
 * @param {*}      retryPayload Deferred-gen payload to persist.
 */
export function startShopifyLogin(email, retryPayload) {
  var returnUrl = window.location.pathname + window.location.search;
  try {
    var pendingState = {
      payload: retryPayload || {},
      returnUrl: returnUrl,
      email: email || '',
      ts: Date.now(),
    };
    localStorage.setItem(PENDING_GEN_KEY, JSON.stringify(pendingState));
    if (email) localStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch (e) { log('pending-gen persist failed', e && e.message); }

  track('shopify_login_redirect', {
    has_pending: !!(retryPayload && retryPayload.petImageUrl),
    has_email: !!email,
  });

  // /account/login is Shopify's universal storefront login entry; for
  // New Customer Accounts it bounces the user through
  // shop.account.myshopify.com for OTP, then returns to return_url.
  // We append email as a hint — Shopify pre-fills it when supported.
  var loginUrl = '/account/login?return_url=' + encodeURIComponent(returnUrl);
  if (email) {
    loginUrl += '&email=' + encodeURIComponent(email);
  }
  window.location.href = loginUrl;
}

/**
 * Boot-time hook: if the customer just completed Shopify's OTP flow and there
 * is a fresh pending payload in localStorage, fire it.
 *
 * Handles three cases:
 *   1) Same page as the gate -> calls callbacks.onResume(augmentedPayload, email).
 *   2) Different page (e.g. /account) -> redirects to saved returnUrl.
 *   3) Stale payload (>30 min) -> silently clears it.
 *
 * Safe to call on every page load (idempotent).
 *
 * Lifted verbatim from monolith maybeResumePostLogin() (lines 968-1040).
 *
 * @param {object}   callbacks
 * @param {function} callbacks.onResume   Called with (augmentedPayload, email)
 *                                        when a fresh pending gen is ready to fire.
 */
export function maybeResumePostLogin(callbacks) {
  var customerId = getCurrentCustomer();
  if (!customerId) return;

  var raw;
  try { raw = localStorage.getItem(PENDING_GEN_KEY); } catch (e) { return; }
  if (!raw) return;

  var pending;
  try { pending = JSON.parse(raw); } catch (e) {
    try { localStorage.removeItem(PENDING_GEN_KEY); } catch (e2) {}
    return;
  }

  // Support legacy shape (bare payload from old magic-link flow) AND
  // new wrapped shape ({payload, returnUrl, email, ts}).
  var payload, returnUrl, savedEmail, ts;
  if (pending && pending.payload && pending.ts) {
    payload    = pending.payload;
    returnUrl  = pending.returnUrl;
    savedEmail = pending.email;
    ts         = pending.ts;
  } else {
    payload    = pending; // legacy: bare retryPayload
    returnUrl  = null;
    savedEmail = null;
    ts         = null;
  }

  if (!payload || !payload.petImageUrl) {
    try { localStorage.removeItem(PENDING_GEN_KEY); } catch (e) {}
    return;
  }

  // Stale -> clear.
  if (ts && (Date.now() - ts) > POST_LOGIN_TTL_MS) {
    log('post-login: pending state stale, clearing');
    try {
      localStorage.removeItem(PENDING_GEN_KEY);
      localStorage.removeItem(PENDING_EMAIL_KEY);
    } catch (e) {}
    return;
  }

  // Wrong page — redirect to original PDP. This handler re-runs there.
  var currentPath = window.location.pathname + window.location.search;
  if (returnUrl && returnUrl !== currentPath) {
    log('post-login: redirecting to original PDP', returnUrl);
    window.location.href = returnUrl;
    return;
  }

  // Right page, logged-in customer, fresh pending payload -> fire it.
  log('post-login: resuming pending gen', { customerId: customerId });
  try {
    localStorage.removeItem(PENDING_GEN_KEY);
    localStorage.removeItem(PENDING_EMAIL_KEY);
  } catch (e) {}

  var emailToUse = savedEmail || (window.WoldenShopifyCustomerEmail || '');
  if (emailToUse) captureEmail(emailToUse);
  track('post_login_gen_resumed', { has_email: !!emailToUse });

  // Stamp the customer id directly onto the payload so the orchestrator
  // creates the new MidjourneyGeneration row with ShopifyCustomerID set
  // at creation time (no waiting on session-merge for the new gen).
  var augmented = Object.assign({}, payload, {
    shopifyCustomerId: customerId,
    email: emailToUse,
    emailCaptured: true,
  });

  var cb = callbacks || {};
  if (cb.onResume) cb.onResume(augmented, emailToUse);
}

// ─── Legacy magic-link email-gate (backward compat) ───────────────────────────

/**
 * Sends a magic-link verification email via the orchestrator.
 * Persists the retry payload to localStorage so the post-verify boot can
 * re-fire the generation.
 *
 * Lifted verbatim from monolith submitEmailCapture() (lines 1041-1079).
 *
 * @param {string}  email
 * @param {*}       retryPayload
 * @param {object}  callbacks
 * @param {HTMLElement|null} callbacks.errEl         Element for inline error text.
 * @param {function}         callbacks.onEmailSent   Called with (email) on success.
 */
export function submitEmailCapture(email, retryPayload, callbacks) {
  var cb = callbacks || {};
  if (cb.errEl) cb.errEl.textContent = '';

  // Persist the deferred gen payload + email so we can re-fire after
  // the customer clicks the magic link in their inbox.
  try {
    localStorage.setItem(PENDING_GEN_KEY, JSON.stringify(retryPayload || {}));
    localStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch (e) { log('pending-gen persist failed', e && e.message); }

  fetch(ORCHESTRATOR_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: ORCHESTRATOR_TOKEN,
      action: 'request-email-verification',
      email: email,
      woldenSessionId: getSessionId(),
      returnUrl: window.location.pathname + window.location.search,
    }),
  }).then(function (resp) {
    return resp.json().then(function (body) { return { resp: resp, body: body }; });
  }).then(function (r) {
    if (!r.resp.ok || !(r.body && r.body.success)) {
      var msg = (r.body && r.body.message) || (r.body && r.body.error) || ('HTTP ' + r.resp.status);
      if (cb.errEl) cb.errEl.textContent = msg;
      return;
    }
    // Email sent — notify caller so it can switch the UI to the
    // "check your inbox" sub-state.
    track('email_verification_requested', { email_domain: (email.split('@')[1] || '').toLowerCase() });
    if (cb.onEmailSent) cb.onEmailSent(email);
  }).catch(function (err) {
    if (cb.errEl) cb.errEl.textContent = 'Connection issue — please try again.';
    log('email-verification request error', err && err.message);
  });
}

/**
 * Boot-time: if the URL contains ?wc_verify_token=<jwt>, POST it to the
 * orchestrator's verify-email-token action. On success, clears the token
 * from the URL and calls callbacks.onVerified(email, deferredPayload).
 *
 * Kept as backward-compat path so magic-link emails already in customer
 * inboxes continue to work after the Shopify-login switchover.
 *
 * Lifted verbatim from monolith maybeConsumeVerifyToken() (lines 1108-1160).
 *
 * @param {object}   callbacks
 * @param {function} callbacks.onVerified  Called with (verifiedEmail, deferredPayload|null).
 * @param {function} callbacks.onToast     Called with (message, opts) for user-visible toasts.
 */
export function maybeConsumeVerifyToken(callbacks) {
  var params = new URLSearchParams(window.location.search || '');
  var token = params.get('wc_verify_token');
  if (!token) return;

  var cb = callbacks || {};
  log('verifying email token from URL');
  if (cb.onToast) cb.onToast('Verifying your email…', { duration: 8000 });

  fetch(ORCHESTRATOR_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: ORCHESTRATOR_TOKEN,
      action: 'verify-email-token',
      verifyToken: token,
    }),
  }).then(function (resp) {
    return resp.json().then(function (body) { return { resp: resp, body: body }; });
  }).then(function (r) {
    // Clean the URL so refresh doesn't re-trigger.
    if (window.history && window.history.replaceState) {
      params.delete('wc_verify_token');
      var qs = params.toString();
      window.history.replaceState({}, '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    }
    if (!r.resp.ok || !(r.body && r.body.success)) {
      if (cb.onToast) cb.onToast('Email verification failed or expired. Please try again.', { duration: 6000 });
      return;
    }
    // Success — persist email + call caller with deferred payload (if any).
    var verifiedEmail = r.body.email || '';
    captureEmail(verifiedEmail);
    track('email_verified', { hasCustomerId: !!getCurrentCustomer() });

    var deferred = null;
    try {
      var raw = localStorage.getItem(PENDING_GEN_KEY);
      deferred = raw ? JSON.parse(raw) : null;
      localStorage.removeItem(PENDING_GEN_KEY);
      localStorage.removeItem(PENDING_EMAIL_KEY);
    } catch (e) {}

    if (cb.onVerified) cb.onVerified(verifiedEmail, deferred && deferred.petImageUrl ? deferred : null);
  }).catch(function (err) {
    log('verify-token error', err && err.message);
    if (cb.onToast) cb.onToast('Email verification failed — please try again.', { duration: 6000 });
  });
}
